// Upload or paste a script file, preview it, then use it and/or save it to the library.

import { useEffect, useMemo, useState } from "react";
import { parseScriptText } from "@cc/shared";
import { cn } from "../../../lib/ui.js";
import { withName } from "../../../lib/scripts.js";
import { Button, Input, Label, Textarea } from "../../ui/primitives.js";
import { LayoutToggle, Roster } from "./tiles.js";

export function ImportPanel({
  seed,
  busy,
  onSave,
  onUse,
}: {
  seed: string;
  busy: boolean;
  onSave: (name: string, json: unknown[]) => void;
  onUse: (json: unknown[]) => void;
}) {
  const [text, setText] = useState(seed);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const parsed = useMemo(() => {
    if (!text.trim()) return null;
    try {
      return { script: parseScriptText(text), json: JSON.parse(text) as unknown[] };
    } catch (err) {
      return { error: err instanceof Error ? err.message : "That doesn't look like a script." };
    }
  }, [text]);

  const script = parsed && "script" in parsed ? parsed.script : null;
  const json = parsed && "json" in parsed ? parsed.json : null;
  // Follow the file's own name until the Storyteller types one of their own.
  useEffect(() => {
    if (script && !nameTouched) setName(script.name);
  }, [script, nameTouched]);

  const unrecognised = script?.characters.filter((c) => c.team === null) ?? [];
  const ready = json !== null && !busy;
  const finalName = name.trim() || script?.name || "Custom Script";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div>
        <Label htmlFor="cc-script-json">Script JSON</Label>
        <Textarea
          id="cc-script-json"
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='Paste a script here, e.g. [{"id":"_meta","name":"My Script"},"washerwoman","imp"]'
          spellCheck={false}
          autoFocus={!seed}
          className="mt-1 font-mono text-xs"
        />
        <p
          className={cn(
            "mt-1 text-xs",
            parsed && "error" in parsed ? "text-evil" : "text-parchment-dim",
          )}
          role="status"
        >
          {!parsed
            ? "Paste JSON above, or use Upload JSON on the left."
            : "error" in parsed
              ? parsed.error
              : `Looks good — ${script!.characters.length} characters.`}
        </p>
      </div>

      {script && (
        <>
          <div>
            <Label htmlFor="cc-import-name">Save as</Label>
            <Input
              id="cc-import-name"
              value={name}
              maxLength={60}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
              className="mt-1"
            />
          </div>
          {unrecognised.length > 0 && (
            <p className="text-xs text-parchment-dim">
              {unrecognised.length} not recognised as official — they stay on the script but get no icon,
              ability text or night order: {unrecognised.map((c) => c.id).join(", ")}.
            </p>
          )}
          <div className="flex items-center justify-between">
            <Label>Roles</Label>
            <LayoutToggle />
          </div>
          <Roster script={script} />
        </>
      )}

      <div className="flex flex-wrap items-center justify-end gap-1.5 border-t border-ink-line pt-3">
        <Button
          variant="ghost"
          disabled={!ready}
          onClick={() => json && onUse(withName(json, finalName))}
        >
          Use without saving
        </Button>
        <Button
          variant="primary"
          disabled={!ready}
          onClick={() => json && onSave(finalName, withName(json, finalName))}
        >
          Save & use
        </Button>
      </div>
    </div>
  );
}
